import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {CrmSyncErrorCodesEnum, CrmSyncErrorModel} from '../../../../../models/api/crmSyncModel';
import {maxErrorCountToShow} from '../crmSyncHelpers';

/*
 * Props.
 */

interface CrmSyncErrorListProps {
  errors: ReadonlyArray<CrmSyncErrorModel>;
  code: string;
  onErrorClick: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledErrorDetailsUl = styled.ul`
  margin: 0;
  padding: 0;
`;

const StyledErrorDetailsLi = styled.li`
  padding: 6px 0px 6px 28px;
  list-style-type: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:hover {
    background-color: ${(p) => p.theme.palette.grey.shade10};
    border-radius: 4px;
  }
`;

const StyledMoreErrorsDiv = styled.div`
  color: ${(p) => p.theme.palette.grey.shade40};
`;

const StyledDotSpan = styled.span`
  font-weight: ${fontWeights.bold};
  margin-right: 8px;
`;

const StyledIcon = styled(Icon)`
  display: none;
  margin-right: 25px;
  color: ${(p) => p.theme.palette.grey.shade40};
  ${StyledErrorDetailsLi}:hover && {
    display: block;
  }
`;

/*
 * Component.
 */

export const CrmSyncErrorList: FC<CrmSyncErrorListProps> = (props) => {
  const {code, errors, onErrorClick} = props;
  const errorsToDisplay = errors.slice(0, maxErrorCountToShow);

  return (
    <StyledErrorDetailsUl key={code}>
      {errorsToDisplay.map((error) => renderErrorDetail(code, error, onErrorClick))}
      {maybeRenderMoreIndicator(errors)}
    </StyledErrorDetailsUl>
  );
};

/*
 * Helpers.
 */

function maybeRenderMoreIndicator(errors: ReadonlyArray<CrmSyncErrorModel>) {
  if (errors.length <= maxErrorCountToShow) {
    return null;
  }
  return (
    <StyledErrorDetailsLi>
      <StyledMoreErrorsDiv>
        <StyledDotSpan>•</StyledDotSpan>
        <Trans id="crm-sync-error-details-and-more">and more...</Trans>
      </StyledMoreErrorsDiv>
    </StyledErrorDetailsLi>
  );
}

function renderErrorDetail(code: string, error: CrmSyncErrorModel, onClick: HandlerOf<string>) {
  const domainText = error.externalResourceName
    ? `${error.reason} (${error.externalResourceName})`
    : error.reason;

  switch (code) {
    case CrmSyncErrorCodesEnum.INVALID_DOMAIN:
    case CrmSyncErrorCodesEnum.COMMON_DOMAIN_USED:
    case CrmSyncErrorCodesEnum.DOMAIN_ALREADY_TAKEN:
      return renderListElement(error, onClick, domainText);
    case CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND:
      return renderListElement(error, onClick, error.externalFieldId);
    case CrmSyncErrorCodesEnum.NAME_NOT_FOUND:
      return renderListElement(error, onClick, error.externalResourceId);
    case CrmSyncErrorCodesEnum.NAME_ALREADY_TAKEN:
    default:
      return renderListElement(error, onClick, error.externalResourceName);
  }
}

function renderListElement(error: CrmSyncErrorModel, onClick: HandlerOf<string>, text?: string) {
  return (
    text && (
      <StyledErrorDetailsLi
        onClick={() => onClick(error.externalResourceId || '')}
        key={error.externalResourceId}
      >
        <div>
          <StyledDotSpan>•</StyledDotSpan>
          {text}
        </div>
        <StyledIcon name="externalLinkSmall" />
      </StyledErrorDetailsLi>
    )
  );
}
